import React from "react";
import { Box, Grid, Link } from "@mui/material";
import theme from "Theme/AppTheme";
import AcxButton from "../AcxButton";
import AddIcon from "@mui/icons-material/Add";
import { AcxLoadingIcon } from "./Icons/AcxLoadingIcon";
import { AcxEmptyIcon } from "./Icons/AcxEmptyIcon";
import { AcxNewIcon } from "./Icons/AcxNewIcon";
import { AcxErrorIcon } from "./Icons/AcxErrorIcon";

export type EmptyStateVariants =
    | "empty"
    | "driversCutover"
    | "topicsCutover"
    | "new"
    | "newSignals"
    | "loading"
    | "error"
    | "modelResultsEmpty"
    | "groupDeleted"
    | "groupAccessDenied";

type Props = {
    size: "large" | "small";
    showButton?: boolean;
    height?: number;
    onButtonClick?: () => void;
    variant: EmptyStateVariants;
    overrideTitle?: string;
    overrideSubTitle?: React.ReactNode;
    overrideButtonText?: string;
    children?: React.ReactNode;
    containerMaxWidth?: string;
};

interface VariantProps {
    icon: React.JSX.Element;
    title: string;
    subTitle: React.ReactNode;
    buttonText: string;
}

export const DRIVERS_HELP_CENTER_URL =
    "https://www.authenticxhelp.com/en/articles/16300101-drivers-sharper-insight-into-every-conversation";

const CutoverSubTitle = () => (
    <Box
        component="span"
        sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            width: "100%",
        }}
    >
        <Box component="span" sx={{ fontWeight: 600 }}>
            Drivers replaced Topics on 7/1/26.
        </Box>
        <Box component="span">
            You can view historical Topics data by adding Topics reports to a
            custom dashboard.
        </Box>
        <Box component="span">
            View our {" "}
            <Link
                href={DRIVERS_HELP_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                fontWeight={600}
            >
                Help Center article
            </Link>{" "}
            for full instructions.
        </Box>
    </Box>
);

const AcxEmptyState = (props: Props) => {
    const imgHeightLoading = props.size === "large" ? 120 : 80;
    const imgWidthLoading = props.size === "large" ? 230 : 153;

    const imgHeight = props.size === "large" ? 120 : 80;
    const imgWidth = props.size === "large" ? 133 : 88;

    const imgHeightNew = props.size === "large" ? 120 : 80;
    const imgWidthNew = props.size === "large" ? 230 : 144;

    const VariantMap: { [K in EmptyStateVariants]: VariantProps } = {
        empty: {
            title: "No Data to Display",
            subTitle: "Adjust your filters and try again.",
            icon: <AcxEmptyIcon height={imgHeight} width={imgWidth} />,
            buttonText: "Add Report",
        },
        driversCutover: {
            title: "No Data to Display",
            subTitle: <CutoverSubTitle />,
            icon: <AcxEmptyIcon height={imgHeight} width={imgWidth} />,
            buttonText: "Add Report",
        },
        topicsCutover: {
            title: "No Data to Display",
            subTitle: <CutoverSubTitle />,
            icon: <AcxEmptyIcon height={imgHeight} width={imgWidth} />,
            buttonText: "Add Report",
        },
        new: {
            title: "Let's Get Started",
            subTitle: "Click to begin building your dashboard.",
            icon: <AcxNewIcon height={imgHeightNew} width={imgWidthNew} />,
            buttonText: "Add Report",
        },
        newSignals: {
            title: "Let's Get Started",
            subTitle: "Click to begin building your dashboard.",
            icon: <AcxNewIcon height={imgHeightNew} width={imgWidthNew} />,
            buttonText: "Add Card",
        },
        loading: {
            title: "Processing your data...",
            subTitle: "This should only take a few moments.",
            icon: (
                <AcxLoadingIcon
                    height={imgHeightLoading}
                    width={imgWidthLoading}
                />
            ),
            buttonText: "Add Report",
        },
        modelResultsEmpty: {
            title: "Check back soon!",
            subTitle:
                "Your report will populate once there are results to view.",
            icon: <AcxNewIcon height={imgHeightNew} width={imgWidthNew} />,
            buttonText: "N/A",
        },
        groupDeleted: {
            title: "Group No Longer Available",
            subTitle:
                "The classifier group for this chart was deleted. Remove this chart or pin a different one.",
            icon: <AcxEmptyIcon height={imgHeight} width={imgWidth} />,
            buttonText: "N/A",
        },
        groupAccessDenied: {
            title: "Models Not Available",
            subTitle:
                "This chart was shared with a group of models you don't have access to.",
            icon: <AcxEmptyIcon height={imgHeight} width={imgWidth} />,
            buttonText: "N/A",
        },
        error: {
            title: "Something Went Wrong",
            subTitle:
                "We ran into an issue loading your data. Try refreshing the page or checking your connection.",
            icon: <AcxErrorIcon height={imgHeight} width={imgWidth} />,
            buttonText: "Add Report",
        },
    };

    const imageComponent = VariantMap[props.variant].icon;

    const title = (
        <Grid
            container
            justifyContent={"center"}
            paddingTop={theme.spacing(3)}
            fontSize={props.size === "large" ? "18px" : "16px"}
            fontWeight={600}
            item
            xs={12}
            flex={0}
        >
            {props.overrideTitle ?? VariantMap[props.variant].title}
        </Grid>
    );

    const subTitle = (
        <Grid
            container
            justifyContent={"center"}
            paddingTop={theme.spacing(1)}
            paddingBottom={props.showButton ? undefined : theme.spacing(3)}
            fontSize={props.size === "large" ? "16px" : "14px"}
            item
            xs={12}
            flex={0}
            textAlign={"center"}
        >
            {props.overrideSubTitle ?? VariantMap[props.variant].subTitle}
        </Grid>
    );

    const buttonComponent = (
        <Grid
            container
            justifyContent={"center"}
            paddingTop={theme.spacing(3)}
            item
            xs={12}
            flex={0}
        >
            <AcxButton startIcon={<AddIcon />} onClick={props.onButtonClick}>
                {props.overrideButtonText ??
                    VariantMap[props.variant].buttonText}
            </AcxButton>
        </Grid>
    );

    return (
        <Grid
            container
            justifyContent={"center"}
            height={props.height}
            alignContent={"center"}
            paddingTop={theme.spacing(2)}
            paddingBottom={theme.spacing(2)}
            maxWidth={props.containerMaxWidth}
        >
            {imageComponent}
            {title}
            {subTitle}
            {props.showButton && buttonComponent}
            {props.children}
        </Grid>
    );
};

export default AcxEmptyState;
